import { Field, Input, Textarea } from '@/shared/components/ui'
import { cn } from '@/shared/lib/cn'

import type { Pregunta, Respuesta } from '../types'

export interface CampoDeBriefProps {
  pregunta: Pregunta
  valor: Respuesta | undefined
  onChange: (valor: Respuesta) => void
  /** Se marca en rojo sólo DESPUÉS de intentar enviar, no mientras se escribe. */
  falta: boolean
}

/**
 * Un campo del brief, del tipo que diga la plantilla.
 *
 * Las tres formas salen del documento de metodología: texto corto para la
 * cabecera, texto largo para las respuestas abiertas y una grilla para las dos
 * secciones que en el PDF son tablas (métricas de éxito y usuarios piloto).
 */
export function CampoDeBrief({ pregunta, valor, onChange, falta }: CampoDeBriefProps) {
  const marca = falta ? { borderColor: 'hsl(var(--overdue))' } : undefined

  if (pregunta.tipo === 'tabla') {
    const filas = normalizarTabla(valor, pregunta)
    return (
      <Field label={etiqueta(pregunta)} hint={pregunta.ayuda} htmlFor={`q-${pregunta.id}-0-0`}>
        <div className="hairline overflow-x-auto rounded-[var(--radius-card)]" style={marca}>
          <table className="w-full text-xs">
            <thead>
              <tr className="hairline-b text-2xs text-text-3">
                {pregunta.columnas.map((columna) => (
                  <th key={columna} className="px-2 py-1.5 text-left font-normal">
                    {columna}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, f) => (
                // La grilla es de tamaño fijo y sus filas no se reordenan ni se
                // borran, así que el índice ES la identidad de la fila.
                <tr key={f} className="hairline-b last:border-b-0">
                  {pregunta.columnas.map((columna, c) => (
                    <td key={columna} className="p-1">
                      <Input
                        id={`q-${pregunta.id}-${String(f)}-${String(c)}`}
                        aria-label={`${columna} ${String(f + 1)}`}
                        value={fila[c] ?? ''}
                        onChange={(event) => {
                          const copia = filas.map((r) => [...r])
                          copia[f]![c] = event.target.value
                          onChange(copia)
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Field>
    )
  }

  const texto = typeof valor === 'string' ? valor : ''

  return (
    <Field label={etiqueta(pregunta)} hint={pregunta.ayuda} htmlFor={`q-${pregunta.id}`}>
      {pregunta.tipo === 'texto' ? (
        <Input
          id={`q-${pregunta.id}`}
          value={texto}
          style={marca}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Textarea
          id={`q-${pregunta.id}`}
          rows={4}
          value={texto}
          style={marca}
          className={cn(falta && 'border-overdue')}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  )
}

/** El asterisco de obligatoria va en el label, no en un color aparte. */
function etiqueta(pregunta: Pregunta): string {
  return pregunta.requerida ? `${pregunta.label} *` : pregunta.label
}

/**
 * Una grilla siempre rectangular, del tamaño que pide la plantilla.
 *
 * Sin esto, escribir en la celda (2,3) de un valor `undefined` explotaría, y un
 * valor guardado con menos columnas que las actuales dejaría celdas sin input.
 */
function normalizarTabla(valor: Respuesta | undefined, pregunta: Pregunta): string[][] {
  const base = Array.isArray(valor) ? valor : []
  const filas = Math.max(pregunta.filas, base.length)
  return Array.from({ length: filas }, (_, f) =>
    Array.from({ length: pregunta.columnas.length }, (_, c) => base[f]?.[c] ?? ''),
  )
}
